import type { LucideIcon } from 'lucide-react';
import type React from 'react';

interface LearnItem {
  question: string;
  answer: string;
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Short Q&A from the page's help file (`emptyStateTitle`/`learnItems` frontmatter). */
  learnItems?: LearnItem[];
  /** The one thing to do next, usually the create button. */
  action?: React.ReactNode;
}

/** What an empty list shows instead of an empty table: what this is, why it is empty, and what to do next. */
export function EmptyState({ icon: Icon, title, description, learnItems, action }: EmptyStateProps) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-surface-alt" aria-hidden="true">
        <Icon size={22} className="text-muted" />
      </div>
      <h3 className="text-lg font-semibold text-heading">{title}</h3>
      <p className="mt-1 text-sm text-muted">{description}</p>

      {action && <div className="mt-5">{action}</div>}

      {learnItems && learnItems.length > 0 && (
        <dl className="mt-8 w-full space-y-3 text-left">
          {learnItems.map((item) => (
            <div key={item.question} className="rounded-lg border border-card-border bg-surface p-4">
              <dt className="text-sm font-medium text-heading">{item.question}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-muted">{item.answer}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
